// Canonical NIP-46 scoped permission string for nostr.gives.
//
// We deliberately scope to a tight kind allowlist. No blanket `sign_event`,
// no kind 3.
//
// Allowlist:
//   1     - text notes (score share grids)
//   30078 - NIP-78 app-specific data (streak backup)
//   27780 - Phase 17 multiplayer lobby "room created" (NIP-16 ephemeral)
//   27781 - Phase 17 multiplayer lobby status flip (NIP-16 ephemeral)
//   30482 - Phase 17 multiplayer move event (NIP-33 parameterised replaceable)
//   30483 - Phase 17 multiplayer game-end summary (NIP-33 parameterised replaceable)
//
// See Vault/Research/Nostr Safety - Follow List Hygiene.md,
// Vault/Architecture/Nostr Integration.md, and
// Vault/Architecture/multiplayer-foundation/Nostr Event Kinds.md.

/** Base (always-on) NIP-46 perms string for the share + backup chokepoint. */
export const NOSTR_AUTH_PERMS_BASE =
  'get_public_key,sign_event:1,sign_event:30078' as const;

/**
 * Default NIP-46 perms string. Equal to {@link NOSTR_AUTH_PERMS_BASE} for
 * the v1 (single-player) deployment. Multiplayer-enabled deployments
 * should request {@link NOSTR_AUTH_PERMS_MULTIPLAYER} via
 * `permsForMultiplayer({ multiplayerEnabled: true })`.
 *
 * Retained as `NOSTR_AUTH_PERMS` for back-compat with existing call sites.
 */
export const NOSTR_AUTH_PERMS = NOSTR_AUTH_PERMS_BASE;

/** Multiplayer kinds added to the allowlist in Phase 17. */
export const MULTIPLAYER_KINDS = [27780, 27781, 30482, 30483] as const;

/**
 * Full multiplayer-aware perms string. The four `sign_event:<kind>` entries
 * are appended to the base perms in the same comma-separated form NIP-46
 * signers expect (clave.casa, nsec.app, Amber will prompt the user per
 * kind on first use; Alby NIP-07 carries over the existing implicit trust
 * model).
 */
export const NOSTR_AUTH_PERMS_MULTIPLAYER =
  `${NOSTR_AUTH_PERMS_BASE},sign_event:27780,sign_event:27781,sign_event:30482,sign_event:30483` as const;

/**
 * Resolve the perms string to advertise in a NIP-46 connect URI.
 *
 * When `multiplayerEnabled` is true the signer will prompt the user
 * for the additional kinds the first time a multiplayer event is signed.
 */
export function permsForMultiplayer(opts: {
  multiplayerEnabled: boolean;
}): string {
  return opts.multiplayerEnabled
    ? NOSTR_AUTH_PERMS_MULTIPLAYER
    : NOSTR_AUTH_PERMS_BASE;
}

export type AllowedKind = 1 | 30078 | 27780 | 27781 | 30482 | 30483;

export const ALLOWED_KINDS: ReadonlySet<number> = new Set<number>([
  1,
  30078,
  27780,
  27781,
  30482,
  30483,
]);

export function isAllowedKind(kind: number): kind is AllowedKind {
  return ALLOWED_KINDS.has(kind);
}

/** Multiplayer subset of the allowlist; useful for routing validators. */
export function isMultiplayerKind(
  kind: number,
): kind is 27780 | 27781 | 30482 | 30483 {
  return (
    kind === 27780 || kind === 27781 || kind === 30482 || kind === 30483
  );
}
